import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistReducer, createMigrate } from 'redux-persist';
import sessionStorage from 'redux-persist/lib/storage/session';
import checkoutReducer from './checkout';
import errorReducer from './error';
import eventsReducer from './events';
import loaderReducer from './loader';
import plansReducer from './plans';
import signupReducer from './signup';
import mapReducer from './map';
import appReducer from './app';
import localeReducer from './locale';
import { mergePersistedTabState, restorePersistedTabState } from './persistSlices';
import { IS_PROD } from '../constants';
import { useDispatch } from 'react-redux';

const migrations = {
  0: (state: any) => ({
    ...state,
  }),
};

const reducers = combineReducers({
  checkout: checkoutReducer,
  error: errorReducer,
  events: eventsReducer,
  loader: loaderReducer,
  plans: plansReducer,
  signup: signupReducer,
  map: mapReducer,
  app: appReducer,
  locale: localeReducer,
});

// A redirect payment (Amazon Pay, Cash App Pay) can return into a fresh tab whose
// sessionStorage is empty; restorePaymentTabState() replays the snapshot taken before leaving.
const rootReducer: typeof reducers = (state, action) =>
  reducers(
    state && restorePersistedTabState.match(action)
      ? (mergePersistedTabState(state, action.payload) as typeof state)
      : state,
    action,
  );

const persistConfig = {
  key: 'root',
  storage: sessionStorage,
  whitelist: ['events', 'signup', 'map', 'plans', 'app', 'checkout', 'locale'],
  version: 0,
  migrate: createMigrate(migrations, { debug: false }),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  devTools: !IS_PROD,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }),
});
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export default store;
